import { Moon, Sun, Stars, Cloud } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";

import { useTheme } from "./ThemeProvider";

export function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleToggle = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  // Prevent hydration mismatch
  if (!mounted) {
    return (
      <div className="relative flex items-center justify-center w-20 h-10 bg-gradient-to-r from-red-200 via-red-100 to-orange-100 dark:from-red-900 dark:via-red-800 dark:to-gray-900 rounded-full p-1 transition-all duration-700 shadow-lg border border-red-200 dark:border-red-500/30">
        <div className="relative z-10 w-8 h-8 rounded-full shadow-lg bg-gradient-to-br from-gray-200 to-gray-300"></div>
      </div>
    );
  }

  return (
    <motion.button
      onClick={handleToggle}
      className="relative flex items-center justify-center w-20 h-10 bg-gradient-to-r from-red-200 via-red-100 to-orange-100 dark:from-red-900 dark:via-red-800 dark:to-gray-900 rounded-full p-1 transition-all duration-700 hover:scale-110 shadow-lg dark:shadow-red-500/25 border border-red-200 dark:border-red-500/30"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
    >
      {/* Animated Sky Background */}
      <motion.div
        className="absolute inset-0 rounded-full overflow-hidden"
        animate={{
          background:
            theme === "dark"
              ? "linear-gradient(135deg, #7f1d1d 0%, #991b1b 50%, #1f2937 100%)"
              : "linear-gradient(135deg, #87ceeb 0%, #98d8e8 50%, #ffd700 100%)",
        }}
        transition={{ duration: 0.7 }}
      />

      {/* Floating Stars (Dark Mode) */}
      <AnimatePresence>
        {theme === "dark" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="absolute inset-0"
          >
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full"
                style={{
                  left: `${20 + Math.random() * 60}%`,
                  top: `${20 + Math.random() * 60}%`,
                }}
                animate={{
                  opacity: [0.3, 1, 0.3],
                  scale: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Clouds (Light Mode) */}
      <AnimatePresence>
        {theme === "light" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="absolute inset-0"
          >
            <motion.div
              className="absolute w-3 h-2 bg-white/60 rounded-full"
              style={{ left: "15%", top: "25%" }}
              animate={{
                x: [0, 2, 0],
                opacity: [0.4, 0.8, 0.4],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="absolute w-2 h-1.5 bg-white/40 rounded-full"
              style={{ right: "20%", top: "40%" }}
              animate={{
                x: [0, -1, 0],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2,
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Moving Sun/Moon Toggle */}
      <motion.div
        className="relative z-10 w-8 h-8 rounded-full shadow-lg"
        animate={{
          x: theme === "light" ? 28 : 0,
          rotate: theme === "light" ? 180 : 0,
        }}
        transition={{
          type: "spring",
          stiffness: 700,
          damping: 30,
          duration: 0.7,
        }}
      >
        <AnimatePresence mode="wait">
          {theme === "light" ? (
            <motion.div
              key="sun"
              initial={{ opacity: 0, scale: 0.5, rotate: -90 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.5, rotate: 90 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 bg-gradient-to-br from-yellow-300 to-orange-400 rounded-full flex items-center justify-center shadow-lg"
            >
              <Sun className="w-5 h-5 text-white" />
              {/* Sun rays */}
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-0.5 h-1 bg-yellow-300 rounded-full"
                  style={{
                    transform: `rotate(${i * 45}deg)`,
                    transformOrigin: "center 20px",
                  }}
                  animate={{
                    opacity: [0.6, 1, 0.6],
                    scale: [0.8, 1.2, 0.8],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.1,
                  }}
                />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="moon"
              initial={{ opacity: 0, scale: 0.5, rotate: 90 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.5, rotate: -90 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center shadow-lg relative overflow-hidden"
            >
              <Moon className="w-5 h-5 text-gray-600" />
              {/* Moon craters */}
              <div className="absolute w-1 h-1 bg-gray-400 rounded-full top-2 left-2 opacity-60" />
              <div className="absolute w-0.5 h-0.5 bg-gray-400 rounded-full bottom-2 right-2.5 opacity-40" />
              <div className="absolute w-0.5 h-0.5 bg-gray-400 rounded-full top-3 right-1.5 opacity-50" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Glowing effect */}
      <motion.div
        className="absolute inset-0 rounded-full"
        animate={{
          boxShadow:
            theme === "dark"
              ? "0 0 20px rgba(192, 34, 40, 0.4), 0 0 40px rgba(192, 34, 40, 0.2), inset 0 0 20px rgba(192, 34, 40, 0.1)"
              : "0 0 20px rgba(255, 193, 7, 0.4), 0 0 40px rgba(255, 193, 7, 0.2), inset 0 0 20px rgba(255, 193, 7, 0.1)",
        }}
        transition={{ duration: 0.7 }}
      />

      {/* Ripple effect on click */}
      <motion.div
        className="absolute inset-0 rounded-full border-2 border-white/30"
        initial={{ scale: 1, opacity: 0 }}
        whileTap={{
          scale: 1.5,
          opacity: [0, 0.6, 0],
          transition: { duration: 0.6 },
        }}
      />
    </motion.button>
  );
}
